import { useState } from "react";
import {
  PackageDashboardHeader,
  PackageSummarySection,
  PackageRevenueOverviewSection,
  PackageRankingsSection,
  PackageBreakdownsSection,
  PackageRevenueStreamsSection,
  getPackageSummaryCards,
  getPackageTrendData,
  getPackageCategoryBreakdown,
  getPackageUserTypeBreakdown,
} from "../../../components/reportDashboard/packages/packages";
import {
  useNobiPackageCategories,
  useNobiPackageBreakdowns,
  useNobiPackageRankings,
  useNobiPackageRevenueStreams,
  useNobiPackageRevenueTrends,
  useNobiPackageSummaryMetrics,
} from "../../../hooks/useReportDashboard";
import { getDateRangeFromFilter } from "../../../utils/reportDashboard/reportDashboardUtils";
import { getErrorMessage } from "../../../utils/error";

const buildTopPackages = (rankingsData) => {
  const topPackages = rankingsData?.data?.topPackages;

  if (Array.isArray(topPackages) && topPackages.length > 0) {
    return topPackages;
  }

  return (rankingsData?.data?.topPartners || []).map((partner, index) => ({
    packageName: partner.packageName || "Nobi Package",
    bookings: partner.bookings || 0,
    revenue: partner.revenue || 0,
    productCode: `NOBI-${index + 1}`,
    averageBookingValue:
      (partner.bookings || 0) > 0
        ? (partner.revenue || 0) / partner.bookings
        : 0,
    cancellationRate: 0,
  }));
};

export function Packages() {
  const [timeRange, setTimeRange] = useState("monthly");
  const dateRange = getDateRangeFromFilter(timeRange);

  const {
    data: categoriesData,
  } = useNobiPackageCategories();

  const {
    data: summaryData,
    isLoading: summaryLoading,
    isError: summaryError,
    error: summaryErrObj,
    refetch: summaryRefetch,
  } = useNobiPackageSummaryMetrics(dateRange);

  const {
    data: trendsData,
    isLoading: trendsLoading,
    isError: trendsError,
    error: trendsErrObj,
    refetch: trendsRefetch,
  } = useNobiPackageRevenueTrends(dateRange);

  const {
    data: streamsData,
    isLoading: streamsLoading,
    isError: streamsError,
    error: streamsErrObj,
    refetch: streamsRefetch,
  } = useNobiPackageRevenueStreams(dateRange);

  const {
    data: breakdownsData,
    isLoading: breakdownsLoading,
    isError: breakdownsError,
    error: breakdownsErrObj,
    refetch: breakdownsRefetch,
  } = useNobiPackageBreakdowns(dateRange);

  const {
    data: rankingsData,
    isLoading: rankingsLoading,
    isError: rankingsError,
    error: rankingsErrObj,
    refetch: rankingsRefetch,
  } = useNobiPackageRankings(dateRange);

  const summaryState = {
    loading: summaryLoading,
    error: summaryError,
    message: getErrorMessage(summaryErrObj),
    onRetry: () => summaryRefetch(),
  };

  const trendsState = {
    loading: trendsLoading,
    error: trendsError,
    message: getErrorMessage(trendsErrObj),
    onRetry: () => trendsRefetch(),
  };

  const streamsState = {
    loading: streamsLoading,
    error: streamsError,
    message: getErrorMessage(streamsErrObj),
    onRetry: () => streamsRefetch(),
  };

  const breakdownsState = {
    loading: breakdownsLoading,
    error: breakdownsError,
    message: getErrorMessage(breakdownsErrObj),
    onRetry: () => breakdownsRefetch(),
  };

  const rankingsState = {
    loading: rankingsLoading,
    error: rankingsError,
    message: getErrorMessage(rankingsErrObj),
    onRetry: () => rankingsRefetch(),
  };

  const summaryCards = getPackageSummaryCards(
    summaryData?.data?.summaryMetrics,
  );
  const monthlyTrend = getPackageTrendData(trendsData?.data?.revenueTrends);
  const categoryBreakdown = getPackageCategoryBreakdown(
    breakdownsData,
    categoriesData?.data,
  );
  const revenueByUserType = getPackageUserTypeBreakdown(breakdownsData);
  const revenueBySupplier = breakdownsData?.data?.revenueBySupplier || [];
  const topDestinations = rankingsData?.data?.topDestinations || [];
  const topPartners = rankingsData?.data?.topPartners || [];
  const topPackages = buildTopPackages(rankingsData);
  const revenueStreams = streamsData?.data?.revenueStreams || [];

  return (
    <div className="space-y-6">
      <PackageDashboardHeader
        timeRange={timeRange}
        onTimeRangeChange={setTimeRange}
      />

      <PackageSummarySection state={summaryState} summaryCards={summaryCards} />

      <PackageRevenueOverviewSection
        trendsState={trendsState}
        breakdownsState={breakdownsState}
        monthlyTrend={monthlyTrend}
        categoryBreakdown={categoryBreakdown}
      />

      <PackageRankingsSection
        state={rankingsState}
        topDestinations={topDestinations}
        topPartners={topPartners}
        topPackages={topPackages}
      />

      <PackageBreakdownsSection
        state={breakdownsState}
        revenueByUserType={revenueByUserType}
        revenueBySupplier={revenueBySupplier}
      />

      <PackageRevenueStreamsSection
        state={streamsState}
        revenueStreams={revenueStreams}
      />
    </div>
  );
}
